/**
 * Calcul du back-in — date de surfaçage estimée (RG-SUP-055).
 *
 * Extrait de `MissionSurfacage.surfacageDateCalc` pour être testable, et
 * enrichi des phases à durée fixe du cycle (drift SEADRF, bottoming SEABTM,
 * flat SEAFLT).
 *
 * Principe : on n'estime pas une durée absolue, on **proratise la durée réelle
 * du cycle précédent** par le ratio des distances de plongée. Les phases à
 * durée fixe ne dépendant pas de la profondeur, elles sont retirées de la
 * durée de référence avant proratisation, puis celles du cycle EN COURS sont
 * ajoutées :
 *
 *   distPlongée = 2·zb + (rate−1)·2·(zb−zt)
 *   durée       = phases(N) + distPlongée(N) × (préDurée − phases(N−1)) / distPlongée(N−1)
 *   backIn      = t(dernier yo) + durée
 *
 * ⚠ Les deux termes de phases portent sur des cycles DIFFÉRENTS : celles à
 * retirer sont celles actives pendant le cycle de référence (N−1), celles à
 * ajouter sont celles actives maintenant (N). Les supposer égales serait faux
 * précisément quand une phase vient d'être activée ou désactivée.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * Source des phases : les tables NMEA (`mission.dataNmea`), PAS le
 * `gli_nmeas` de `gliderstate`. Ce dernier est un INSTANTANÉ (dernière trame
 * reçue par type) : il ne dit rien du cycle précédent, et il conserve une
 * trame périmée quand la phase est désactivée. Les tables NMEA sont indexées
 * par `yoCycle` et répondent pour n'importe quel cycle.
 *
 * **Deux conditions** pour qu'une phase compte sur un cycle donné :
 *
 * 1. la trame a une ligne à ce cycle ;
 * 2. sa date (`TRM_DATTRM`) n'est pas antérieure à celle de la SEAMRS du MÊME
 *    cycle. Les trames sont émises en train, SEAMRS en tête : une trame plus
 *    ancienne que la SEAMRS du cycle est un reliquat d'un train précédent — la
 *    phase a été désactivée avant la plongée, IRIS a simplement cessé de
 *    l'émettre.
 *
 * La règle 2 est celle qu'applique déjà `MissionNmeaDetail.hideDetail` pour
 * masquer DRF/BTM/WPT/DEV/ALR.
 *
 * Colonnes (cf `docs/functional/websockets-wob-sample.md` §NMEA) :
 * `SDR_DATA_TO` (min), `SBT_DATA_TO` (min), `SFT_DATA_TIMEOUT`.
 *
 * ⚠ La doc ne précise PAS l'unité de `SFT_DATA_TIMEOUT` (« Timeout
 * d'activation float »). Traitée en minutes comme les deux autres, selon la
 * spec fonctionnelle — à confirmer sur un glider en flat.
 */
import { AloYo } from '@/models/alo/AloYo'
import { AloDataRawSet } from '@/models/alo/AloDataRawSet'

/** Clés `dataNmea` nécessaires au calcul. */
export const FIXED_PHASE_SECTION_KEYS = ['seamrs', 'seadrf', 'seabtm', 'seaflt']

/** Sections NMEA nécessaires — `undefined` = pas (encore) chargée. */
export interface IFixedPhaseSections {
  seamrs?: AloDataRawSet
  seadrf?: AloDataRawSet
  seabtm?: AloDataRawSet
  seaflt?: AloDataRawSet
}

const DATE_COLUMN = 'trm_dattrm'

/** Lit une valeur de row par nom de colonne, insensible à la casse. */
function readColumn(section: AloDataRawSet, row: Array<any>, dbName: string): unknown {
  const target = dbName.toLowerCase()
  const col = section.columns?.find(c => c.dbName?.toLowerCase() === target)
  return col ? row[col.pos] : undefined
}

function toTime(v: unknown): number | null {
  if (typeof v !== 'string' && typeof v !== 'number') return null
  const t = new Date(v).getTime()
  return isNaN(t) ? null : t
}

/** Durée IRIS en minutes → nombre fini strictement positif, sinon 0. */
function toMinutes(v: unknown): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN
  return isFinite(n) && n > 0 ? n : 0
}

/**
 * Durée d'une phase au cycle demandé, `0` si la phase n'était pas active.
 *
 * @param refTime date de la SEAMRS du même cycle, ou `null` si indisponible —
 *                le filtre de fraîcheur n'est alors pas appliqué (parité v1,
 *                qui ne masque que si la date SEAMRS existe).
 */
function phaseMinutesAtCycle(
  section: AloDataRawSet | undefined,
  valueColumn: string,
  cycle: number,
  refTime: number | null,
): number {
  const row = section?.dataSet?.[cycle]
  if (!section || !row) return 0

  const frameTime = toTime(readColumn(section, row, DATE_COLUMN))
  // Pas de date exploitable → trame considérée absente (parité v1 hideDetail).
  if (frameTime === null) return 0
  if (refTime !== null && frameTime < refTime) return 0

  return toMinutes(readColumn(section, row, valueColumn))
}

/** Somme en millisecondes des phases à durée fixe actives au cycle donné. */
export function fixedPhasesMsAtCycle(sections: IFixedPhaseSections, cycle: number | null): number {
  if (cycle === null || cycle === undefined) return 0

  const mrsRow = sections.seamrs?.dataSet?.[cycle]
  const refTime = sections.seamrs && mrsRow ? toTime(readColumn(sections.seamrs, mrsRow, DATE_COLUMN)) : null

  const minutes =
    phaseMinutesAtCycle(sections.seadrf, 'SDR_DATA_TO', cycle, refTime) +
    phaseMinutesAtCycle(sections.seabtm, 'SBT_DATA_TO', cycle, refTime) +
    phaseMinutesAtCycle(sections.seaflt, 'SFT_DATA_TIMEOUT', cycle, refTime)

  return minutes * 60000
}

/** Distance parcourue en plongée sur un cycle, dérivée du profil du yo. */
function diveDistance(yo: AloYo): number {
  const zb = yo.zb ?? 0
  const zt = yo.zt ?? 0
  const rate = yo.rate ?? 0
  return 2 * zb + (rate - 1) * 2 * (zb - zt)
}

/** Somme en ms des phases à durée fixe actives au cycle donné. */
export type PhasesMsAtCycle = (cycle: number | null) => number

const NO_PHASES: PhasesMsAtCycle = () => 0

/**
 * Date de surfaçage estimée, ou `null` si non calculable — l'appelant se
 * rabat alors sur le `backInDateTime` fourni par le serveur.
 *
 * @param yos        yos de la mission, ordre chronologique (`mission.yoArray`)
 * @param phasesMsAt durées des phases fixes par cycle (défaut : aucune phase)
 */
export function computeBackInDate(yos: Array<AloYo>, phasesMsAt: PhasesMsAtCycle = NO_PHASES): Date | null {
  if (yos.length <= 1) return null

  const lastYo = yos[yos.length - 1]

  // Remonte au dernier yo RÉELLEMENT surfacé : les yos sous l'eau d'un cycle
  // multi-yo ne portent ni profil ni horodatage exploitables.
  let idx = 2
  let prevYo: AloYo | null = null
  while (yos.length >= idx && (prevYo = yos[yos.length - idx]).isUnderWater) {
    idx++
  }
  if (!prevYo || prevYo.isUnderWater) return null

  const prevDist = diveDistance(prevYo)
  if (prevDist === 0) return null

  const lastTime = lastYo.yoGpsDatTime.getTime()
  // Fin du cycle de référence = premier point de drift du dernier yo s'il y en
  // a un (le drift de surface suit immédiatement le surfaçage), sinon le yo.
  const referenceEnd = (lastYo.driftArray[0] || lastYo).yoGpsDatTime.getTime()
  const prevTime = prevYo.yoGpsDatTime.getTime()
  if (isNaN(lastTime) || isNaN(referenceEnd) || isNaN(prevTime)) return null

  // Part proportionnelle à la profondeur du cycle de référence, une fois ses
  // propres phases fixes retirées. Clampée à 0 : un cycle entièrement occupé
  // par des phases fixes ne doit pas produire une durée négative.
  const proportionalRef = Math.max(0, referenceEnd - prevTime - phasesMsAt(prevYo.yosCycle ?? null))

  const duration = phasesMsAt(lastYo.yosCycle ?? null) + (diveDistance(lastYo) * proportionalRef) / prevDist
  if (!isFinite(duration)) return null

  return new Date(lastTime + duration)
}
